import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Subject } from 'rxjs';
import { CommHubService } from '../comm-hub/comm-hub.service';
import { SocketService } from '../socket/socket.service';
import { NotificationAttachment } from 'src/app/shared/models/notification-attachment.model';
import { NotificationAttachmentService } from 'src/app/shared/services/notification-attachment.service';

export type NotificationCategory = 'general' | 'alert' | 'announcement' | 'reminder';

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  category: NotificationCategory;
  priority: 'normal' | 'high' | 'critical';
  read: boolean;
  readAt?: string;
  createdAt: Date;
  source: 'server' | 'realtime';
  senderEmail?: string;
  senderName?: string;
  location?: string;
  fileName?: string;
  fileType?: string;
  attachments?: NotificationAttachment[];
  enableDownload?: boolean;
  isPinned?: boolean;
}

export interface NotificationStats {
  total: number;
  unread: number;
  critical: number;
  byCategory: Record<string, number>;
}

const MAX_IN_MEMORY = 300;
const PAGE_LIMIT = 50;

@Injectable({ providedIn: 'root' })
export class NotificationHubService {
  private itemsSubject = new BehaviorSubject<NotificationItem[]>([]);
  private unreadSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadingMoreSubject = new BehaviorSubject<boolean>(false);
  private importantModalSubject = new Subject<NotificationItem[]>();
  private importantQueue: NotificationItem[] = [];
  private loading = false;
  private loadingMore = false;
  private loadGeneration = 0;
  private lastLoadAt = 0;
  private lastLoadCategory: string | undefined;
  private nextCursor: string | null = null;
  private hasMoreSubject = new BehaviorSubject<boolean>(false);
  /** Cross-category snapshot for tab unread badges (not the visible list). */
  private badgeItems: NotificationItem[] = [];

  items$ = this.itemsSubject.asObservable();
  unreadCount$ = this.unreadSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  loadingMore$ = this.loadingMoreSubject.asObservable();
  hasMore$ = this.hasMoreSubject.asObservable();
  importantModalRequested$ = this.importantModalSubject.asObservable();

  constructor(
    private commHub: CommHubService,
    private socketService: SocketService,
    private messageService: MessageService,
    private attachmentService: NotificationAttachmentService
  ) {
    this.socketService.getNotification().subscribe((data) => {
      if (data) this.prependRealtime(this.mapSocketPayload(data));
    });
    this.socketService.getBroadcast().subscribe((data) => {
      if (data) this.prependRealtime(this.mapSocketPayload(data, 'announcement'));
    });
    this.socketService.connected$.subscribe((ready) => {
      if (ready) {
        this.softRefreshOnReconnect();
      }
    });
  }

  get items(): NotificationItem[] {
    return this.itemsSubject.value;
  }

  get nextPageCursor(): string | null {
    return this.nextCursor;
  }

  /** Items used for tab/header unread math across category switches. */
  getBadgeItems(): NotificationItem[] {
    return this.badgeItems;
  }

  load(category?: string): void {
    if (this.loading || this.loadingMore) return;

    const now = Date.now();
    if (now - this.lastLoadAt < 2000 && this.lastLoadCategory === category && this.items.length > 0) {
      return;
    }

    this.fetchPage(category, null, false);
  }

  loadMore(category?: string): void {
    if (this.loading || this.loadingMore || !this.nextCursor) return;
    this.fetchPage(category, this.nextCursor, true);
  }

  refresh(category?: string): void {
    this.lastLoadAt = 0;
    this.nextCursor = null;
    this.fetchPage(category, null, false);
  }

  refreshUnreadBadge(): void {
    this.commHub.getUnreadCount().subscribe({
      next: (res) => {
        if (res?.count != null) {
          this.unreadSubject.next(res.count);
        }
      },
      error: () => { /* ignore */ }
    });
  }

  markRead(item: NotificationItem): void {
    if (item.read) return;
    item.read = true;
    item.readAt = new Date().toISOString();
    this.syncBadgeItemRead(item.id, true, item.readAt);
    this.itemsSubject.next([...this.items]);
    this.adjustUnread(-1);
    if (item.id && !item.id.startsWith('rt-')) {
      this.commHub.markNotificationRead(item.id).subscribe({
        next: () => this.refreshUnreadBadge(),
        error: () => { /* ignore */ }
      });
    }
  }

  markAllRead(category?: string): void {
    const now = new Date().toISOString();
    let changed = 0;
    this.items.forEach((item) => {
      if (item.read) return;
      if (category && item.category !== category) return;
      item.read = true;
      item.readAt = now;
      changed += 1;
    });
    this.badgeItems.forEach((item) => {
      if (item.read) return;
      if (category && item.category !== category) return;
      item.read = true;
      item.readAt = now;
    });
    if (changed) {
      this.itemsSubject.next([...this.items]);
      this.adjustUnread(-changed);
    }
    this.commHub.markAllNotificationsRead(category).subscribe({
      next: () => this.refreshUnreadBadge(),
      error: () => { /* ignore */ }
    });
  }

  clearImportantQueue(): void {
    this.importantQueue = [];
  }

  pinItem(item: NotificationItem, pinned = true): void {
    item.isPinned = pinned;
    this.commHub.pinNotification(item.id, pinned).subscribe({ error: () => { /* ignore */ } });
    this.itemsSubject.next(this.mergeAndSort([...this.items]));
    this.badgeItems = this.mergeAndSort(
      this.badgeItems.map((b) => (b.id === item.id ? { ...b, isPinned: pinned } : b))
    ).slice(0, MAX_IN_MEMORY);
  }

  getStats(filtered: NotificationItem[]): NotificationStats {
    const unread = filtered.filter((i) => !i.read).length;
    const critical = filtered.filter((i) => !i.read && i.category === 'alert').length;
    const byCategory: Record<string, number> = {};
    filtered.forEach((i) => {
      if (!i.read) byCategory[i.category] = (byCategory[i.category] || 0) + 1;
    });
    return { total: filtered.length, unread, critical, byCategory };
  }

  filterItems(items: NotificationItem[], query: string, category: string): NotificationItem[] {
    let result = [...items];
    if (category && category !== 'all') {
      result = result.filter((i) => i.category === category);
    }
    if (query?.trim()) {
      const q = query.toLowerCase();
      result = result.filter((i) => i.title.toLowerCase().includes(q) || i.body.toLowerCase().includes(q));
    }
    return result.sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
  }

  getAttachments(item: NotificationItem): NotificationAttachment[] {
    if (item.attachments?.length) {
      return item.attachments;
    }
    return this.attachmentService.toAttachmentsFromLegacy(
      item.location,
      item.fileName,
      item.fileType
    );
  }

  private softRefreshOnReconnect(): void {
    if (!this.items.length) {
      this.load();
      return;
    }
    this.refreshUnreadBadge();
    this.commHub.loadNotifications({
      category: this.lastLoadCategory,
      limit: PAGE_LIMIT
    }).subscribe({
      next: (res) => {
        const serverItems = (res?.items || []).map((r: any) => this.mapServerItem(r));
        // Server wins: last write in mergeAndSort.
        const merged = this.mergeAndSort([...this.items, ...serverItems]).slice(0, MAX_IN_MEMORY);
        this.itemsSubject.next(merged);
        this.mergeIntoBadgeItems(serverItems, !this.lastLoadCategory);
        if (res?.unreadCount != null) {
          this.unreadSubject.next(res.unreadCount);
        }
        if (res?.nextCursor !== undefined) {
          this.nextCursor = res.nextCursor;
          this.hasMoreSubject.next(!!res.nextCursor);
        }
      },
      error: () => { /* ignore */ }
    });
  }

  private fetchPage(category: string | undefined, cursor: string | null, append: boolean): void {
    this.loadGeneration += 1;
    const gen = this.loadGeneration;

    if (append) {
      this.loadingMore = true;
      this.loadingMoreSubject.next(true);
    } else {
      this.loading = true;
      this.loadingSubject.next(true);
      // Supersede any in-flight load-more.
      if (this.loadingMore) {
        this.loadingMore = false;
        this.loadingMoreSubject.next(false);
      }
    }

    this.socketService.connectIfNeeded();

    this.commHub.loadNotifications({
      category,
      cursor: cursor || undefined,
      limit: PAGE_LIMIT
    }).subscribe({
      next: (res) => {
        if (gen !== this.loadGeneration) {
          return;
        }
        this.lastLoadAt = Date.now();
        this.lastLoadCategory = category;
        const serverItems = (res?.items || []).map((r: any) => this.mapServerItem(r));
        if (append) {
          const merged = this.mergeAndSort([...this.items, ...serverItems]).slice(0, MAX_IN_MEMORY);
          this.itemsSubject.next(merged);
          this.mergeIntoBadgeItems(serverItems, false);
        } else {
          const realtimeOnly = this.items.filter(
            (i) => i.source === 'realtime' && !serverItems.find((s) => s.id === i.id)
          );
          const display = this.mergeAndSort([...realtimeOnly, ...serverItems]).slice(0, MAX_IN_MEMORY);
          this.itemsSubject.next(display);
          if (!category) {
            this.badgeItems = display;
          } else {
            this.mergeIntoBadgeItems(serverItems, false);
          }
        }
        this.nextCursor = res?.nextCursor ?? null;
        this.hasMoreSubject.next(!!this.nextCursor);
        if (res?.unreadCount != null) {
          this.unreadSubject.next(res.unreadCount);
        }
        this.finishLoad(append);
      },
      error: () => {
        if (gen !== this.loadGeneration) {
          return;
        }
        this.finishLoad(append);
      }
    });
  }

  private finishLoad(append: boolean): void {
    if (append) {
      this.loadingMore = false;
      this.loadingMoreSubject.next(false);
    } else {
      this.loading = false;
      this.loadingSubject.next(false);
    }
  }

  private mergeIntoBadgeItems(serverItems: NotificationItem[], replaceAll: boolean): void {
    if (replaceAll) {
      this.badgeItems = this.mergeAndSort(serverItems).slice(0, MAX_IN_MEMORY);
      return;
    }
    this.badgeItems = this.mergeAndSort([...this.badgeItems, ...serverItems]).slice(0, MAX_IN_MEMORY);
  }

  private syncBadgeItemRead(id: string, read: boolean, readAt?: string): void {
    const idx = this.badgeItems.findIndex((b) => b.id === id);
    if (idx >= 0) {
      this.badgeItems[idx] = { ...this.badgeItems[idx], read, readAt };
    }
  }

  private adjustUnread(delta: number): void {
    this.unreadSubject.next(Math.max(0, this.unreadSubject.value + delta));
  }

  private mapServerItem(r: any): NotificationItem {
    const location = r.attachmentUrl;
    const fileName = r.attachmentName;
    const fileType = r.attachmentType;
    const category = this.normalizeCategory(r.category);
    return {
      id: r.id,
      title: r.title || 'Notification',
      body: r.body || '',
      category,
      priority: category === 'alert' ? 'critical' : 'normal',
      read: !!r.read,
      readAt: r.readAt,
      createdAt: new Date(r.createdAt || Date.now()),
      source: 'server',
      senderEmail: r.senderEmail,
      senderName: r.senderName,
      location,
      fileName,
      fileType,
      attachments: this.attachmentService.toAttachmentsFromLegacy(location, fileName, fileType),
      enableDownload: !!location,
      isPinned: r.isPinned
    };
  }

  private mapSocketPayload(data: any, category: NotificationCategory = 'general'): NotificationItem {
    const location = data.attachmentUrl || data.location;
    const fileName = data.attachmentName || data.fileName;
    const fileType = data.attachmentType || data.fileType;
    const resolvedCategory = this.normalizeCategory(data.category || category);
    return {
      id: data.id || data.eventId || `rt-${Date.now()}`,
      title: data.title || 'Notification',
      body: data.body || data.message || '',
      category: resolvedCategory,
      priority: resolvedCategory === 'alert' ? 'critical' : 'normal',
      read: false,
      createdAt: new Date(data.createdAt || data.timestamp || Date.now()),
      source: 'realtime',
      senderEmail: data.senderEmail,
      senderName: data.senderName,
      location,
      fileName,
      fileType,
      attachments: this.attachmentService.toAttachmentsFromLegacy(location, fileName, fileType),
      enableDownload: !!location,
      isPinned: false
    };
  }

  private prependRealtime(item: NotificationItem): void {
    if (this.items.some((i) => i.id === item.id)) return;
    const next = this.mergeAndSort([item, ...this.items]).slice(0, MAX_IN_MEMORY);
    this.itemsSubject.next(next);
    if (!this.badgeItems.some((b) => b.id === item.id)) {
      this.badgeItems = this.mergeAndSort([item, ...this.badgeItems]).slice(0, MAX_IN_MEMORY);
    }
    this.adjustUnread(1);
    this.showToast(item);
    if (item.category === 'alert' || item.category === 'announcement') {
      this.importantQueue.push(item);
      this.importantModalSubject.next([...this.importantQueue]);
    }
  }

  private showToast(item: NotificationItem): void {
    this.messageService.add({
      severity: item.category === 'alert' ? 'error' : 'info',
      summary: item.title,
      detail: (item.body || '').slice(0, 120),
      life: 5000
    });
  }

  private mergeAndSort(list: NotificationItem[]): NotificationItem[] {
    const map = new Map<string, NotificationItem>();
    list.forEach((i) => map.set(i.id, i));
    return Array.from(map.values()).sort((a, b) => {
      if (a.isPinned !== b.isPinned) return Number(b.isPinned) - Number(a.isPinned);
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
  }

  private normalizeCategory(category?: string): NotificationCategory {
    if (category === 'info') { return 'general'; }
    if (category === 'alert' || category === 'announcement' || category === 'reminder') {
      return category;
    }
    return 'general';
  }
}
